import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { ErrorLoad, MessageWrap, Paginator, StepCSSTransitionGroup, Table, TableBody } from '@wg/wows-react-uikit';

import { root } from '~/preloaded';
import settings from '~/settings';
import { REQUEST_STATUSES, REQUESTS_TABS } from '~/constants';
import { isChatDenied } from '~/helpers/account';
import { t } from '~/helpers/localization';
import useMount from '~/hooks/useMount';
import {
  ClanIsDisbandedTooltip,
  ClanMembersInfoMissingTooltip,
  MemberInfoMissingTooltip,
  MemberIsBannedTooltip,
  RankTooltipContent,
} from '~/tooltips';

import ApplicationsNonClanMemberTableItem from '~/Components/Requests/Applications/ApplicationsNonClanMemberTableItem';
import InvitesClanMemberTableItem from '~/Components/Requests/Invites/InvitesClanMemberTableItem';
import { EntriesTableHead } from '~/UIKit';
import { Tooltip } from '~/UIKit/components';

import type { NavigateFunction } from 'react-router-dom';
import type { IClanApplication, IMemberApplication } from '~/Actions/ActionApplications';
import type { ClanType, IClanInvite } from '~/Actions/ActionInvites';
import type { IApplicationsState } from '~/Reducers/ReducerApplications';
import type { IInvitesState } from '~/Reducers/ReducerInvites';
import type { ICurrentAccountState } from '~/store/slices/currentAccountSlice';

type EntriesHeaderItem = {
  text: string;
  isSortable: boolean;
  name: string;
  modify: string;
  glyph?: string;
};

type CommonProps = {
  currentAccount: ICurrentAccountState;
  headers: EntriesHeaderItem[];
  navigate: NavigateFunction;

  fetchEntriesByCurrentState: (withGlobalSpinner: boolean) => void;
  fetchEntriesByPage: (page: number) => void;

  onReloadClick: () => void;
  onSortClick: (order: string, isAsc: boolean) => void;
};

type InvitesProps = CommonProps & {
  entryType: REQUESTS_TABS.INVITES;
  entries: IInvitesState;
  isCurrentClanFull?: boolean;

  sendApplication?: never;
};

type ApplicationsProps = CommonProps & {
  entryType: REQUESTS_TABS.APPLICATION;
  entries: IApplicationsState;

  sendApplication: (clan: ClanType) => void;

  isCurrentClanFull?: never;
};

type Props = InvitesProps | ApplicationsProps;

const NonOperationalEntries = (props: Props) => {
  const { currentAccount, entries, entryType, headers, navigate } = props;

  const appRef = useRef<HTMLElement | null>(null);
  const [fetchByNewPage, setFetchByNewPage] = useState(false);

  const withGlobalSpinner = useCallback(() => {
    return entries.error === null && isEmpty(entries[entryType]) && isEmpty(entries.meta);
  }, [entries, entryType]);

  useMount(() => {
    appRef.current = document.getElementById('app');

    props.fetchEntriesByCurrentState(withGlobalSpinner());

    if (appRef.current) {
      appRef.current.scrollTop = 0;
    }
  });

  // Track previous page
  const prevPageRef = useRef(entries.page);
  useEffect(() => {
    setFetchByNewPage(prevPageRef.current !== entries.page);
    prevPageRef.current = entries.page;
  }, [entries.page]);

  // Scroll after local fetching
  const prevFetchingRef = useRef(entries.isFetching);
  const prevErrorRef = useRef(entries.error);

  useEffect(() => {
    const wasFetching = prevFetchingRef.current;
    const wasError = prevErrorRef.current;

    const isScrollAfterLocalFetching = wasFetching && !entries.isFetching && wasError === null;

    if (isScrollAfterLocalFetching && appRef.current) {
      appRef.current.scrollTop = 0;
    }

    prevFetchingRef.current = entries.isFetching;
    prevErrorRef.current = entries.error;
  }, [entries.isFetching, entries.error]);

  const renderError = useCallback(() => {
    const message =
      entryType === REQUESTS_TABS.INVITES
        ? t('Не удалось получить информацию по приглашениям.')
        : t('Не удалось получить информацию по заявкам.');

    return <ErrorLoad isFlat message={message} onReloadClick={props.onReloadClick} />;
  }, [entryType, props.onReloadClick]);

  const renderEntries = useCallback(() => {
    if (entryType === REQUESTS_TABS.INVITES) {
      const list = entries[entryType] as IClanInvite[];

      return (
        <TableBody>
          {list.map((invite) => {
            const hasActiveInviteForAccount =
              invite.status === REQUEST_STATUSES.ACTIVE ||
              list.some((item) => {
                return (
                  item.status === REQUEST_STATUSES.ACTIVE &&
                  'account' in item &&
                  'account' in invite &&
                  item.account.id === invite.account.id
                );
              });

            return (
              <InvitesClanMemberTableItem
                key={invite.id}
                invite={invite}
                isChatDenied={isChatDenied(currentAccount, invite.account)}
                isCurrentClanFull={props.isCurrentClanFull}
                hasActiveInviteForAccount={hasActiveInviteForAccount}
              />
            );
          })}
        </TableBody>
      );
    }

    const list = entries[entryType] as IMemberApplication[];

    return (
      <TableBody>
        {list.map((application) => {
          const hasActiveApplicationForClan =
            application.status === REQUEST_STATUSES.ACTIVE ||
            list.some((item) => {
              return (
                item.status === REQUEST_STATUSES.ACTIVE &&
                'clan' in item &&
                'clan' in application &&
                item.clan.id === application.clan.id
              );
            });

          return (
            <ApplicationsNonClanMemberTableItem
              key={application.id}
              navigate={navigate}
              application={application}
              currentAccount={currentAccount}
              sendApplication={props.sendApplication}
              hasActiveApplicationForClan={hasActiveApplicationForClan}
              root={root}
            />
          );
        })}
      </TableBody>
    );
  }, [currentAccount, entries, entryType, navigate, props]);

  const renderPaginator = useCallback(() => {
    if (entries.meta) {
      return (
        <Paginator
          key="paginator"
          page={entries.page}
          total={entries.meta.total || 1}
          limit={settings.entries.limit}
          isFetching={fetchByNewPage}
          isDisabled={entries.isFetching}
          onSelectedPage={props.fetchEntriesByPage}
        />
      );
    }

    return null;
  }, [entries.isFetching, entries.meta, entries.page, fetchByNewPage, props.fetchEntriesByPage]);

  const getRankTooltipContentCb = useCallback(
    (tooltipTip: React.ReactNode) => {
      if (!tooltipTip) {
        return null;
      }

      const memberId = Number(tooltipTip);
      const list = entries[entryType] as IClanApplication[];
      const entry = list.find((e) => e.account.id === memberId);

      if (!entry) {
        return null;
      }

      return (
        <RankTooltipContent
          rank={entry.statistics.rank}
          seasonId={entry.statistics.season_id}
          seasonRank={entry.statistics.season_rank}
        />
      );
    },
    [entries, entryType],
  );

  const isGlobalFetching = entries.isFetching;
  const total = get({ entries }, 'entries.meta.total', 0);
  const isErrored = entries.error !== null;
  const inviteDelay = 28;

  if (isGlobalFetching) {
    return null;
  }

  if (total === 0 && !isErrored && !entries.isFetching) {
    let title = t('Нет отправленных заявок');
    let message = t('Последние %(inviteDelay)s дней вы не отправляли заявки на вступление в клан.', {
      inviteDelay,
    });

    if (entryType === REQUESTS_TABS.INVITES) {
      title = t('Нет отправленных приглашений');
      message = t('Последние %(inviteDelay)s дней ваш клан не отправлял приглашения игрокам.', {
        inviteDelay,
      });
    }

    return (
      <StepCSSTransitionGroup level={1}>
        <MessageWrap title={title} message={message} key="nonoperational-message-empty" />
      </StepCSSTransitionGroup>
    );
  }

  return (
    <StepCSSTransitionGroup level={1}>
      <Table
        key="table"
        isFetching={entries.isFetching}
        isErrored={isErrored}
        stickyContainerId="non-operational-entries-sticky-container"
      >
        <EntriesTableHead
          entryType={entryType}
          sort={entries.sort}
          headers={headers}
          stickyContainerId="non-operational-entries-sticky-container"
          onSortClick={props.onSortClick}
        />
        {isErrored ? renderError() : renderEntries()}
      </Table>

      {renderPaginator()}

      <ClanIsDisbandedTooltip />
      <ClanMembersInfoMissingTooltip />
      <MemberIsBannedTooltip />
      <MemberInfoMissingTooltip />

      <Tooltip id="rank-tooltip" getContent={getRankTooltipContentCb} />
    </StepCSSTransitionGroup>
  );
};

export default memo(NonOperationalEntries);
